import styled from '@emotion/styled';
import * as Sentry from '@sentry/browser';
import { useFlags } from 'launchdarkly-react-client-sdk';
import React, { useEffect, useMemo, useState } from 'react';
import type { ApolloError } from 'apollo-client';
import { CalloutInline, Link } from '@modernhealth/vital-components';
import { useTranslation } from 'react-i18next';

import { BackArrow } from 'src/modules/components/BackArrow';
import { ProviderType } from 'src/modules/user/models/user';
import { CenteredSpinner } from 'src/shared/components/CenteredSpinner';
import { ContactUsModal } from 'src/shared/components/ContactUsModal';
import { color, font, layout, mediaQueries } from 'src/shared/styles/variables';
import { ProviderFragment } from '../documents/getMyProvidersQuery.generated';
import { useSubmitFeatureReviewMutation } from '../documents/submitFeatureReviewMutation.generated';
import { useTimeToCareLabelsMutation } from '../documents/timeToCareLabelsMutation.generated';
import { ContactUsBanner } from './ContactUsBanner';
import { DemographicFeedbackModal } from './DemographicFeedbackModal';
import { DemographicFeedbackThanksModal } from './DemographicFeedbackThanksModal';

import { PreferencesButton } from './PreferencesButton';
import { PreferencesModal } from './PreferencesModal';
import { ProviderAvailabilityOption } from './ProviderAvailability';
import { ProviderCard } from './ProviderCard';
import { colors } from '@modernhealth/vital-foundation';
import { captureException } from 'src/shared/helpers/captureException';

const Wrapper = styled.div({
    display: 'flex',
    flexDirection: 'column',
    marginBottom: 30,
    maxWidth: layout.containerBiggerWidth,
    maxHeight: 'auto',
    alignItems: 'center',
    [mediaQueries.sm]: {
        marginTop: 50,
    },
});

const Container = styled.div({
    display: 'flex',
    justifyContent: 'center',
    width: '100%',
});

const TitleContainer = styled.div({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
});

const Title = styled.h3({
    color: color.pickledBluewood,
    ...font.black(font.size.extraLarge),
    marginBottom: 0,
});

const Subtitle = styled.h5({
    color: color.steelGray,
    marginTop: 10,
    marginBottom: 10,
    ...font.roman(font.size.mediumExtended),
});

const ErrorWrapper = styled.div({
    padding: 24,
    width: '100%',
});

const LinkButton = styled.button({
    appearance: 'none',
    border: 'none',
    background: 'none',
    boxShadow: 'none',
    padding: 0,
});

interface ProvidersListWrapperProps {
    centerContent: boolean;
}

const ProvidersListWrapper = styled.ul<ProvidersListWrapperProps>(props => ({
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: props.centerContent ? 'center' : 'space-between',
    maxWidth: 1236,
    marginTop: 32,
    padding: 0,
    [mediaQueries.lg]: {
        flexDirection: 'column',
        justifyContent: 'center',
    },
}));

export interface Props {
    selectedProvider: string | undefined;
    onProviderPress: (uuid: string) => void;
    providerTypeToDisplay?: ProviderType;
    headerText: string;
    subTitleText: string;
}

export interface MatchedProviderSelectionProps extends Props {
    showFilters?: boolean;
    loading?: boolean;
    error?: ApolloError;
    onBackPress?: () => void;
    showBackButton?: boolean;
    matchedProviders?: ProviderFragment[];
    notMatchedProviders?: ProviderFragment[];
    refetchMatchedProviders?: (races: string[]) => void;
    teletherapyMode?: boolean;
}

export enum ProviderSelectionTestID {
    ShowMoreButton = 'ProviderSelection.ShowMoreButton',
    GoToTopButton = 'ProviderSelection.GoToTopButton',
    ContactUsSection = 'ProviderSelection.ContactUsSection',
    ContactUsModal = 'ProviderSelection.ContactUsModal',
    NoProviderModal = 'ProviderSelection.NoProviderModal',
}

export const MatchedProviderSelection: React.FC<MatchedProviderSelectionProps> = ({
    onProviderPress,
    selectedProvider,
    loading = false,
    error,
    showFilters = false,
    matchedProviders,
    notMatchedProviders,
    onBackPress,
    showBackButton = false,
    teletherapyMode = false,
    headerText,
    subTitleText,
    refetchMatchedProviders,
}) => {
    const { t } = useTranslation();
    const [submitFeatureReview] = useSubmitFeatureReviewMutation();

    const { explorePageConnectWithSupport } = useFlags();

    const [fetchTTClabel, { data, loading: isTimeToCareLoading }] = useTimeToCareLabelsMutation();

    const [showFeedbackModal, setShowFeedbackModal] = useState(false);
    const [showFeedbackThanksModal, setShowFeedbackThanksModal] = useState(false);
    const [isFeedbackSubmitted, setIsFeedbackSubmitted] = useState(false);

    const [modalVisible, setModalVisible] = useState(false);
    const [races, setRaces] = useState<string[]>([]);

    const [preferenceModalVisibile, setPreferenceModalVisibile] = useState(false);

    // BE may optimize to respond with record<uuid, avilability> to skip normalization here 6/30/21

    useEffect(() => {
        const allProviders = matchedProviders?.concat(notMatchedProviders ?? []) ?? [];

        if (allProviders.length > 0) {
            const ttcProviderUUIDs = allProviders.reduce((acc, provider) => {
                acc.push(provider.uuid);
                return acc;
            }, [] as string[]);

            fetchTTClabel({
                variables: {
                    providersList: {
                        providerUuids: ttcProviderUUIDs,
                    },
                },
            });
        }
    }, [fetchTTClabel, matchedProviders, notMatchedProviders]);

    const uuidAvailabilityHash = useMemo(() => {
        const ttcLabelList = data?.timeToCareLabels?.providersWithTtcLabels;

        return (
            ttcLabelList?.reduce((acc, ttc) => {
                if (!ttc?.providerUuid || !ttc?.providerLabel) return acc;

                acc[ttc.providerUuid] = ProviderAvailabilityOption[ttc.providerLabel] ?? undefined;

                return acc;
            }, {} as Record<string, ProviderAvailabilityOption | undefined>) ?? undefined
        );
    }, [data?.timeToCareLabels?.providersWithTtcLabels]);

    useEffect(() => {
        const allProviders: ProviderFragment[] =
            matchedProviders?.concat(notMatchedProviders ?? []) ?? [];

        if (allProviders.length > 0) {
            const ttcProviderUUIDs = allProviders.reduce((acc, provider) => {
                acc.push(provider.uuid);
                return acc;
            }, [] as string[]);

            handleFetchTTClabel(ttcProviderUUIDs);
        }
    }, [matchedProviders, notMatchedProviders]);

    const handleFetchTTClabel = async providersList => {
        try {
            await fetchTTClabel({
                variables: {
                    providersList: {
                        providerUuids: [...providersList],
                    },
                },
            });
        } catch (error) {
            Sentry.withScope(scope => {
                scope.setExtra('error_info', 'Error: failed to make TTC Label mutation');
                Sentry.captureException(error);
            });
            captureException(error, { error_info: 'Error: Failed to make TTC Label mutation' });
        }
    };

    const handleFeedbackSubmit = async answers => {
        try {
            await submitFeatureReview({
                variables: {
                    featureReview: {
                        ...answers,
                    },
                },
            });
        } catch (e) {
            Sentry.withScope(scope => {
                scope.setExtra('error_info', 'Error: failed to submit feature review');

                Sentry.captureException(e);
            });
            captureException(error, { error_info: 'Failed to submit feature review' });
        }
    };

    const hasNoProvidersToShow =
        (!matchedProviders || matchedProviders.length === 0) &&
        (!notMatchedProviders || notMatchedProviders.length === 0);

    return (
        <Container>
            <Wrapper>
                <TitleContainer>
                    {showBackButton ? (
                        <BackArrow
                            onClick={onBackPress}
                            style={{
                                marginLeft: 24,
                            }}
                        />
                    ) : (
                        <div />
                    )}
                    <Title>{headerText}</Title>
                    <div />
                </TitleContainer>
                <Subtitle>{subTitleText}</Subtitle>
                {error && hasNoProvidersToShow && (
                    <ErrorWrapper>
                        <CalloutInline
                            heading={t('messaging:generic_error.title')}
                            variant="danger"
                        >
                            <Link>
                                <LinkButton onClick={() => refetchMatchedProviders?.(races)}>
                                    {t('programs:programs.library.try_again')}
                                </LinkButton>
                            </Link>
                        </CalloutInline>
                    </ErrorWrapper>
                )}
                {loading && <CenteredSpinner color={colors.blue75} size={64} />}
                {showFilters && !hasNoProvidersToShow && races.length > 0 && (
                    <PreferencesButton
                        onClick={() => {
                            setIsFeedbackSubmitted(false);
                            setPreferenceModalVisibile(!preferenceModalVisibile);
                        }}
                        filtersCount={races.length}
                    />
                )}
                {showFilters && (
                    <PreferencesModal
                        onToggle={() => {
                            setPreferenceModalVisibile(!preferenceModalVisibile);
                        }}
                        onApply={choices => {
                            refetchMatchedProviders?.(choices);
                        }}
                        onPressFeedback={() => {
                            setPreferenceModalVisibile(false);
                            setShowFeedbackModal(true);
                        }}
                        choices={races}
                        setChoices={setRaces}
                        visible={preferenceModalVisibile}
                        isFeedbackSubmitted={isFeedbackSubmitted}
                    />
                )}

                <DemographicFeedbackModal
                    onSubmit={answers => {
                        handleFeedbackSubmit(answers);
                        setShowFeedbackModal(false);
                        setShowFeedbackThanksModal(true);
                    }}
                    onBack={() => {
                        setShowFeedbackModal(false);
                        setPreferenceModalVisibile(true);
                    }}
                    visible={showFeedbackModal}
                />
                <DemographicFeedbackThanksModal
                    onSubmit={() => {
                        setIsFeedbackSubmitted(true);
                        setShowFeedbackThanksModal(false);
                        setPreferenceModalVisibile(true);
                    }}
                    visible={showFeedbackThanksModal}
                />

                {!loading && matchedProviders && matchedProviders.length > 0 ? (
                    <ProvidersListWrapper centerContent>
                        {matchedProviders.slice(0, 9).map((provider, i) => (
                            <ProviderCard
                                key={provider.uuid}
                                isTimeToCareLoading={isTimeToCareLoading}
                                providerAvailability={uuidAvailabilityHash?.[provider.uuid]}
                                provider={provider}
                                loading={selectedProvider === provider.uuid}
                                onPress={onProviderPress}
                                bestMatch={i === 0}
                                showLicensedStates={teletherapyMode}
                            />
                        ))}
                    </ProvidersListWrapper>
                ) : null}

                {(hasNoProvidersToShow || explorePageConnectWithSupport) && !loading && (
                    <ContactUsBanner
                        teletherapyMode={teletherapyMode}
                        onClick={() => setModalVisible(true)}
                        demographicsMode
                        hasMatchedProviders={matchedProviders && matchedProviders.length > 0}
                        hasUnmatchedProviders={
                            notMatchedProviders && notMatchedProviders.length > 0
                        }
                    />
                )}

                {!loading && notMatchedProviders && notMatchedProviders.length > 0 ? (
                    <ProvidersListWrapper centerContent>
                        {notMatchedProviders.slice(0, 9).map((provider, i) => (
                            <ProviderCard
                                key={provider.uuid}
                                isTimeToCareLoading={isTimeToCareLoading}
                                providerAvailability={uuidAvailabilityHash?.[provider.uuid]}
                                provider={provider}
                                loading={selectedProvider === provider.uuid}
                                onPress={onProviderPress}
                                bestMatch={
                                    i === 0 && matchedProviders && matchedProviders.length === 0
                                }
                                showLicensedStates={teletherapyMode}
                            />
                        ))}
                    </ProvidersListWrapper>
                ) : null}

                <ContactUsModal
                    dataTestID={ProviderSelectionTestID.ContactUsModal}
                    visible={modalVisible}
                    onToggle={() => setModalVisible(isModalVisible => !isModalVisible)}
                    tags={[`selection_flow_contact_us_modal`]}
                    helpDesk
                />
            </Wrapper>
        </Container>
    );
};
